<template>
  <div class="justify-start flex ps-2 pb-2">
    <NText
      weight="medium"
      variant="caption1"
      class="text-neutral-secondary-press"
    >
      {{ t('symbol.marketDepth') }}
    </NText>
  </div>

  <div class="flex flex-col h-full">
    <NBox class="ps-1 h-[calc(100%-4.25rem)] pb-7">
      <MarketDepthTableHeader class="mt-1" />

      <div class="flex max-h-full">
        <div v-bind="containerProps" class="flex-1">
          <div v-bind="wrapperProps">
            <template
              v-for="item in list"
              :key="`${symbol.isin}-${item.index}`"
            >
              <MarketDepthTableItem
                class="flex-1"
                :buy="buyMarketDepth[item.index]"
                :sell="sellMarketDepth[item.index]"
                :lower-price-threshold="symbol.lowerPriceThreshold ?? 0"
                :upper-price-threshold="symbol.upperPriceThreshold ?? 0"
                :reference-price="symbol.referencePrice ?? 0"
                :is-option="isOption"
                :max-volume="maxVolume"
                :row-index="item.index"
                :total-volume-calculator-fn="totalVolumeCalculator"
                @volume-click="handleVolumeClick"
                @price-click="handlePriceClick"
              />
            </template>
          </div>
        </div>
      </div>
    </NBox>

    <NBox class="flex my-1 px-2 py-1.5">
      <NPopper
        v-if="totalVolumesPercentage.buy"
        placement="top"
        hover
        offset-y="8"
      >
        <div
          class="bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 rounded-r-sm cursor-pointer"
          :class="{ 'rounded-l-sm': !totalVolumesPercentage.sell }"
          :style="{ width: `${totalVolumesPercentage.buy}%` }"
        >
          <NText>
            <NumberFormatter
              #="{ number }"
              maximum-fraction-digits="2"
              number-style="percent"
              :value="totalVolumesPercentage.buy / 100"
            >
              <NText variant="caption2" weight="medium">
                {{ number }}
              </NText>
            </NumberFormatter>
          </NText>
        </div>

        <template #content>
          <div
            class="bg-neutral-quaternary px-2 py-1 rounded-md text-neutral-primary"
            dir="ltr"
          >
            <NumberFormatter
              #="{ number }"
              notation="compact"
              notation-limit="999999"
              maximum-fraction-digits="2"
              :value="totalCount.buy"
            >
              <NText
                variant="caption2"
                weight="medium"
                class="text-neutral-quaternary"
              >
                {{ number }} *
              </NText>
            </NumberFormatter>

            <NumberFormatter
              #="{ number }"
              notation="compact"
              notation-limit="999999"
              maximum-fraction-digits="2"
              :value="totalVolumes.buy"
            >
              <NText
                variant="caption2"
                weight="medium"
                class="text-neutral-quaternary"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </template>
      </NPopper>

      <NPopper
        v-if="totalVolumesPercentage.sell"
        placement="top"
        hover
        offset-y="8"
      >
        <div
          class="bg-critical-primary darkTheme:bg-opacity-30 bg-opacity-20 rounded-l-sm cursor-pointer mr-0.5"
          :class="{ 'rounded-r-sm': !totalVolumesPercentage.sell }"
          :style="{ width: `${totalVolumesPercentage.sell}%` }"
        >
          <NText>
            <NumberFormatter
              #="{ number }"
              maximum-fraction-digits="2"
              number-style="percent"
              :value="totalVolumesPercentage.sell / 100"
            >
              <NText variant="caption2" weight="medium">
                {{ number }}
              </NText>
            </NumberFormatter>
          </NText>
        </div>

        <template #content>
          <div
            class="bg-neutral-quaternary px-2 py-1 rounded-md text-neutral-primary"
            dir="ltr"
          >
            <NumberFormatter
              #="{ number }"
              notation="compact"
              notation-limit="999999"
              maximum-fraction-digits="2"
              :value="totalCount.sell"
            >
              <NText
                variant="caption2"
                weight="medium"
                class="text-neutral-quaternary"
              >
                {{ number }} *
              </NText>
            </NumberFormatter>

            <NumberFormatter
              #="{ number }"
              notation="compact"
              notation-limit="999999"
              maximum-fraction-digits="2"
              :value="totalVolumes.sell"
            >
              <NText
                variant="caption2"
                weight="medium"
                class="text-neutral-quaternary"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </template>
      </NPopper>
    </NBox>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { MarketDepthItems } from '@libs/api-reference';

import { useLiveSymbolQuery } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';
import MarketDepthTableHeader from './MarketDepthTableHeader.vue';
import MarketDepthTableItem from './MarketDepthTableItem.vue';
import { ORDER_TYPES } from '@core/order/src';
import { useVirtualList } from '@vueuse/core';

type MarketDepthTable = {
  buyMarketDepth: MarketDepthItems[];
  sellMarketDepth: MarketDepthItems[];
  lowerPriceThreshold: number;
  upperPriceThreshold: number;
  isin: string;
  isOption?: boolean;
};

const props = defineProps<MarketDepthTable>();

const { t } = useI18n();

const arrayWithRequiredRowsLength = computed(() => {
  const numberOfRows = Math.max(
    props.buyMarketDepth?.length,
    props.sellMarketDepth?.length
  );
  return Array.from({ length: numberOfRows });
});

const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(
  arrayWithRequiredRowsLength,
  {
    itemHeight: 24,
  }
);

const { openNewOrderFormDialog } = useOrderDialogs();

const { symbol } = useLiveSymbolQuery(toRef(props, 'isin'), {
  immediate: false,
});

watch(symbol, (curr, prev) => {
  const isSymbolChanged = curr.isin !== prev.isin;
  if (!isSymbolChanged) return;

  scrollTo(0);
});

const nonDisabledBuyMarketDepth = computed(() =>
  props.buyMarketDepth.filter((item) => !isDisabled(item.price))
);
const nonDisabledSellMarketDepth = computed(() =>
  props.sellMarketDepth.filter((item) => !isDisabled(item.price))
);

const sellValidRange = computed(() => {
  return nonDisabledSellMarketDepth.value.filter(
    (x: MarketDepthItems) => x.volume
  );
});

const buyValidRange = computed(() => {
  return nonDisabledBuyMarketDepth.value.filter(
    (x: MarketDepthItems) => x.volume
  );
});

const maxVolume = computed(() => {
  const maxSellVolume = Math.max.apply(
    Math,
    sellValidRange.value.map((o: MarketDepthItems) => {
      return o.volume;
    })
  );

  const maxBuyVolume = Math.max.apply(
    Math,
    buyValidRange.value.map((o: MarketDepthItems) => {
      return o.volume;
    })
  );

  return Math.max(maxSellVolume, maxBuyVolume);
});

const totalVolumes = computed(() => {
  const totalSellVolume = calcSellVolume(props.sellMarketDepth.length - 1);
  const totalBuyVolume = calcBuyVolume(props.buyMarketDepth.length - 1);

  return {
    sell: totalSellVolume,
    buy: totalBuyVolume,
  };
});
const totalVolumesPercentage = computed(() => {
  const totalVolume = totalVolumes.value.sell + totalVolumes.value.buy;

  return {
    sell: (totalVolumes.value.sell / totalVolume) * 100,
    buy: (totalVolumes.value.buy / totalVolume) * 100,
  };
});
const totalCount = computed(() => ({
  buy:
    nonDisabledBuyMarketDepth.value.reduce(
      (sum, item) => sum + item.count,
      0
    ) ?? 0,
  sell:
    nonDisabledSellMarketDepth.value.reduce(
      (sum, item) => sum + item.count,
      0
    ) ?? 0,
}));

const isDisabled = (item) => {
  return (
    !props.isOption &&
    (item < props.lowerPriceThreshold || item > props.upperPriceThreshold)
  );
};

const calcBuyVolume = (itemIndex: number) => {
  let quantity = 0;

  for (let i = 0; i <= itemIndex; i++) {
    const item = nonDisabledBuyMarketDepth.value[i];

    if (item) {
      quantity += item.volume;
    }
  }

  return quantity;
};

const calcSellVolume = (itemIndex: number) => {
  let quantity = 0;

  for (let i = 0; i <= itemIndex; i++) {
    const item = nonDisabledSellMarketDepth.value[i];

    if (item) {
      quantity += item.volume;
    }
  }

  return quantity;
};

function totalVolumeCalculator(orderType: string, index: number) {
  return orderType === ORDER_TYPES.buy
    ? calcBuyVolume(index)
    : calcSellVolume(index);
}

function handlePriceClick(price: number, orderType: string) {
  if (isDisabled(price)) return;

  openNewOrderFormDialog({
    type: orderType === ORDER_TYPES.buy ? ORDER_TYPES.sell : ORDER_TYPES.buy,
    price,
    symbol: {
      isin: symbol.value.isin as string,
      instrumentName: symbol.value.instrumentName as string,
      instrumentTypeCode: symbol.value.instrumentTypeCode as string,
    },
  });
}

function handleVolumeClick(price: number, orderType: string, index: number) {
  if (isDisabled(price)) return;

  let quantity = 0;
  const property =
    orderType === ORDER_TYPES.sell ? sellValidRange.value : buyValidRange.value;

  for (let i = 0; i <= index; i++) {
    quantity += property[i].volume;
  }

  openNewOrderFormDialog({
    type: orderType === ORDER_TYPES.buy ? ORDER_TYPES.sell : ORDER_TYPES.buy,
    price,
    quantity,
    symbol: {
      isin: symbol.value.isin as string,
      instrumentName: symbol.value.instrumentName as string,
      instrumentTypeCode: symbol.value.instrumentTypeCode as string,
    },
  });
}
</script>
